/* =====================================================
   <details-gesture>
   - Smooth drag-to-close for <details>
   - Adds 'is-dragging' while pulling
   - Keeps menu visible until fully out of view
   - Opacity stays 1 while dragging
   - Adds momentum + overshoot easing
===================================================== */
if (!customElements.get('details-gesture')) {
  class DetailsGesture extends HTMLElement {
    constructor() {
      super();
      this.active = false;
      this.startY = 0;
      this.deltaY = 0;
      this.velocityY = 0;
      this.lastY = 0;
      this.lastTime = 0;
      this.hasMoved = false;
      this.isAnimating = false;

      // Configurable thresholds
      this.threshold = parseFloat(this.getAttribute('data-threshold')) || 160;
      this.velocityThreshold = parseFloat(this.getAttribute('data-velocity-threshold')) || 1;
      this.moveThreshold = parseFloat(this.getAttribute('data-move-threshold')) || 10;
      this.toggleClass = this.getAttribute('data-toggle-class') || 'menu-open';
      this.closeDuration = parseFloat(this.getAttribute('data-close-duration')) || 600;
      this.dragClass = this.getAttribute('data-drag-class') || 'is-dragging';
    }

    connectedCallback() {
      this.style.touchAction = 'none';
      this.style.display = 'block';
      this.style.width = '100%';
      this.style.height = '28px';
      this.style.position = 'relative';
      this.style.cursor = 'grab';

      this.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
      });

      this.addEventListener('pointerdown', this.#onStart, { passive: false });
      this.addEventListener('pointermove', this.#onMove, { passive: false });
      this.addEventListener('pointerup', this.#onEnd);
      this.addEventListener('pointercancel', this.#onEnd);
      this.addEventListener('lostpointercapture', this.#onEnd);
    }

    #getDetails() {
      return this.closest('details');
    }

    #getContent() {
      return this.parentElement;
    }

    #onStart = (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (this.isAnimating) return;
      this.active = true;
      this.hasMoved = false;

      this.startY = e.clientY;
      this.lastY = e.clientY;
      this.lastTime = performance.now();
      this.deltaY = 0;
      this.velocityY = 0;

      this.setPointerCapture(e.pointerId);

      const details = this.#getDetails();
      if (details) details.classList.add(this.dragClass);
    };

    #onMove = (e) => {
      if (!this.active || this.isAnimating) return;

      const now = performance.now();
      const deltaY = e.clientY - this.startY;
      const dy = e.clientY - this.lastY;
      const dt = now - this.lastTime;

      this.velocityY = dy / Math.max(dt, 1);
      this.lastY = e.clientY;
      this.lastTime = now;
      this.deltaY = deltaY;

      if (!this.hasMoved && Math.abs(deltaY) > this.moveThreshold) {
        this.hasMoved = true;
      }

      if (!this.hasMoved) return;
      e.preventDefault();
      e.stopPropagation();

      const content = this.#getContent();
      if (!content) return;

      if (deltaY > 0) {
        content.style.transform = `translateY(${deltaY}px)`;
        // content.style.opacity = `${Math.max(0.4, 1 - deltaY / 250)}`; 
        content.style.opacity = '1';
      }
    };

    #onEnd = (e) => {
      if (!this.active) return;
      this.active = false;
      this.releasePointerCapture?.(e.pointerId);
      e.preventDefault();
      e.stopPropagation();

      const details = this.#getDetails();
      const content = this.#getContent();
      if (!details || !content) return;

      const deltaY = this.deltaY;
      const fastSwipe = this.velocityY > this.velocityThreshold;
      const shouldClose = deltaY > this.threshold || fastSwipe;

      // Momentum distance (how far it continues if velocity high)
      const momentumDistance = Math.min(Math.max(this.velocityY * 200, 0), 200);

      if (shouldClose && deltaY > 0) {
        this.isAnimating = true;
          
        // Always slide completely off screen
        const totalDistance = window.innerHeight;

        const duration = Math.max(300, Math.min(700, 700 - this.velocityY * 300));
        // content.style.transition = `transform ${duration}ms cubic-bezier(0.22, 1, 0.36, 1)`;
        content.style.transition = `transform ${this.closeDuration}ms cubic-bezier(0.22, 1, 0.36, 1)`;
        content.style.transform = `translateY(${totalDistance}px)`;
        content.style.opacity = '1';

        setTimeout(() => {
          content.style.transition = '';
          content.style.transform = '';
          details.removeAttribute('open');
          if (this.toggleClass) details.classList.remove(this.toggleClass);
          details.classList.remove(this.dragClass);
          this.isAnimating = false;
        }, this.closeDuration);
      } else {
        // Overshoot bounce back
        content.style.transition = 'transform 400ms cubic-bezier(0.34, 1.56, 0.64, 1)';
        content.style.transform = 'translateY(0)';
        content.style.opacity = '1';
        setTimeout(() => {
          content.style.transition = '';
          details.classList.remove(this.dragClass);
        }, 400);
      }
    };
  }

  customElements.define('details-gesture', DetailsGesture);
}